import { supabase } from "@/lib/supabase";
import { isBillingModeLocked, lockStateFromCounts } from "@/lib/upsell-billing-mode-lock";

/**
 * VIB-994: sjekk om `billing_mode` kan endres for et mersalg. Låst så snart
 * mersalget har timeføring (mot en av mersalgets budsjettlinjer) eller en
 * faktura (T&M: `invoices.upsell_id`; fastpris-arv: `added_to_invoice_id`).
 * Bounded reads (scopet til ett mersalgs egne linjer/id) — OK for
 * paginate-gaten. Kastes fra mutationFn FØR selve oppdateringen, så en stale
 * UI ikke kan smette forbi vernet.
 */
export async function assertBillingModeChangeAllowed(
  upsellId: string,
  addedToInvoiceId: string | null,
): Promise<void> {
  // .limit(1000): defensivt tak (matcher PostgREST-defaulten eksplisitt) —
  // ett mersalg har i praksis et fåtall linjer, aldri i nærheten av 1000.
  const { data: lines, error: linesErr } = await supabase
    .from("budget_lines")
    .select("id")
    .eq("upsell_id", upsellId)
    .limit(1000);
  if (linesErr) throw linesErr;
  const lineIds = (lines ?? []).map((l) => l.id as string);

  let timeEntryCount = 0;
  if (lineIds.length > 0) {
    const { count, error: teErr } = await supabase
      .from("time_entries")
      .select("id", { count: "exact", head: true })
      .in("budget_line_id", lineIds)
      .is("deleted_at", null);
    if (teErr) throw teErr;
    timeEntryCount = count ?? 0;
  }

  const { count: invoiceCount, error: invErr } = await supabase
    .from("invoices")
    .select("id", { count: "exact", head: true })
    .eq("upsell_id", upsellId);
  if (invErr) throw invErr;

  const locked = isBillingModeLocked(
    lockStateFromCounts({
      timeEntryCount,
      invoiceCount: invoiceCount ?? 0,
      hasLegacyInvoiceLink: !!addedToInvoiceId,
    }),
  );
  if (locked) {
    throw new Error(
      "Faktureringsform er låst: mersalget har registrert timeføring eller faktura, og kan ikke endres.",
    );
  }
}
